import type { Nullable } from '@src/shared/types/common';

window.kendoWindowOptions = {
    draggable: true,
    scrollable: true,
    visible: false,
    animation: false,
    resizable: true
};

function setControlData(control: kendo.ui.ComboBox, dataFunc: () => Record<string, unknown>, action: string) {
    const read = control.dataSource.options.transport?.read;

    if (read && typeof read === 'object') {
        read.data = dataFunc;
        if (action) {
            read.url = action;
        }
    }
}

const VALUES_ALL = { MS: -1, CB: '-1' } as const;
const YES_ID = 1;
const NO_ID = 0;

function isBinaryYesNoComboBoxData(dataItems: ArrayLike<{ Value?: string | number }>): boolean {
    return dataItems.length === 3
        && String(dataItems[0].Value) === VALUES_ALL.CB
        && String(dataItems[1].Value) === String(YES_ID)
        && String(dataItems[2].Value) === String(NO_ID);
}

function resolveFilterComboBoxValue(obj: kendo.ui.ComboBox): Nullable<string | boolean> {
    const value = obj.value();
    const dataItems = obj.dataItems();

    if (value === VALUES_ALL.CB) {
        return null;
    }
    else if (isBinaryYesNoComboBoxData(dataItems)) {
        return value === String(YES_ID);
    }

    return value;
}

function resolveFilterDatePickerValue(obj: kendo.ui.DatePicker): Nullable<string> {
    const value = obj.value();
    return value ? kendo.toString(value, 'dd.MM.yyyy') : null;
}

function autoInitControls() {
    $('[data-role]').each((_, element) => {
        const elem = $(element);
        const controlName = elem.attr('id');
        const kendoTypeName = Object
            .getOwnPropertyNames(elem.data())
            .find((item) => (
                item.startsWith('kendo')
                && item !== 'kendoPopup'
                && item !== 'kendoStaticList'
            ));

        if (controlName && kendoTypeName) {
            window[controlName] = elem.data(kendoTypeName);
        }
    });

    $('textarea:not(.k-content)').each((_, textarea) => {
        const $textarea = $(textarea);
        const controlName = $textarea.attr('id');

        if (controlName) {
            // Для Kendo TextArea храним инстанс виджета (у него есть метод value()),
            // для обычных textarea — jQuery-обёртку (читается через val()).
            window[controlName] = $textarea.getKendoTextArea() ?? $textarea;
        }
    });

    $('.k-checkbox').each((_, input) => {
        const $input = $(input);
        const controlName = $input.attr('id');

        if (controlName) {
            window[controlName] = $input;
        }
    });
}

function initConfigureComboBox() {
    $('[data-role=combobox]').each((_, element) => {
        configureComboBoxOpening(element);
    });
}

function configureComboBoxOpening(element: HTMLElement) {
    const control = $(element).data('kendoComboBox');
    const $input = control?.input;

    if (element.hasAttribute('data-set-focus-event')) {
        return;
    }

    if ($input) {
        $input.on('click', () => {
            if ($input.prop('disabled')) {
                return;
            }
            control.open();
        });

        element.setAttribute('data-set-focus-event', '');
    }

    $input?.attr('autocomplete', 'off');
}

function initInputFocus() {
    const $allInputs = $('input:not(:has(+ [data-role=numerictextbox]))');

    $allInputs.on('focus', ({ currentTarget }) => {
        const $currentInput = $(currentTarget);
        const role = $currentInput.data().role;
        if (role === 'combobox' || role === 'multiselect') {
            return;
        }
        queueMicrotask(() => $currentInput.trigger('select'));
    });
}

function setControlsSettings() {
    const PHONE_NUMBER_INVALID_CHARS_REGEX = /[^0-9()\s\-+]/g;
    const STANDARD_TIME_REGEX = /^(?:[01]\d|2[0-3]):?[0-5]\d$|^(?:[01]\d|2[0-3])[0-5]\d$/;
    const WORKLOAD_TIME_REGEX = /^(?:\d{2}|100):[0-5]\d$/;
    const filesGridDiv = $('#grFiles');

    if (filesGridDiv.length === 1) {
        filesGridDiv.on('mousedown', 'tr[role="row"]', window.gridRowRightClick);
    }

    initConfigureComboBox();

    initializeDateWidgets('datepicker', 'kendoDatePicker');
    initializeDateWidgets('datetimepicker', 'kendoDateTimePicker');

    $('[data-role=timepicker]').each((_, element) => {
        const widget = $(element).data('kendoTimePicker');
        const $input = widget?.element;

        if (element.hasAttribute('data-set-focus-event')
            || widget?.element.attr('aria-disabled') === 'true') {
            return;
        }

        if ($input) {
            window.validateTimeInput($input, STANDARD_TIME_REGEX);
            $input.on('click', () => widget.open());
            element.setAttribute('data-set-focus-event', '');
        }
    });

    initInputFocus();

    $('[data-validator="time-cost"]').each((_, elementWrapper) => {
        const $inputs = $(elementWrapper).find('input[data-role="combobox"]');
        const widget = $inputs.data('kendoComboBox');
        const $input = widget?.element;

        if ($input) {
            if ($input.data('set-validator')) {
                return;
            }
            window.validateTimeInput($input, WORKLOAD_TIME_REGEX);
            $input.data('set-validator', true);
        }
    });

    $('[data-intl-tel-input-id]').each((_, input: HTMLInputElement) => {
        if (input.getAttribute('aria-disabled') === 'true') {
            return;
        }
        const iti = window.getIntlTelInputInstance(input);
        input.addEventListener('change', window.validatePhoneInput.bind(input, iti));
    });

    $('[data-validator="phone"]').each((_, input: HTMLInputElement) => {
        window.validatePhoneTextBox($(input), PHONE_NUMBER_INVALID_CHARS_REGEX);
    });
}

function toggleVisibleWindow(instance: kendo.ui.Window) {
    instance.options.visible ? instance.close() : instance.open();
}

/**
 * Функция, которая переписывает поведение DatePicker/DateTimePicker компонента
 * Требование Антона, чтобы при открытом календаре была возможность управлять кареткой в input
 * Тем самым перезаписываю начальное поведение метода dateView.move
 * @Private
 * */
function onMoveRewriteDatePickerHandler(instance: kendo.ui.DatePicker) {
    const arrowKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];

    return (event: JQuery.KeyDownEvent) => {
        if (instance.dateView.popup.visible()) {
            if (arrowKeys.includes(event.code)) {
                return false;
            }
            if (event.code === 'Escape') {
                instance.close();
                return true;
            }
        }

        return true;
    };
}

function initializeDateWidgets(role: string, widgetType: string) {
    $(`[data-role=${role}]`).each((_, element) => {
        const widget: kendo.ui.DatePicker | undefined = $(element).data(widgetType);
        const $input = widget?.element;

        if (element.hasAttribute('data-set-focus-event')) {
            return;
        }

        if ($input && $input.attr('aria-selected') !== 'true') {
            window.validateDateInput($input);
            $input.on('click', () => {
                widget.open();
            });
            widget.dateView.move = onMoveRewriteDatePickerHandler(widget);
            element.setAttribute('data-set-focus-event', '');
        }
    });
}

window.setControlsSettings = setControlsSettings;
window.autoInitControls = autoInitControls;
window.resolveFilterComboBoxValue = resolveFilterComboBoxValue;
window.resolveFilterDatePickerValue = resolveFilterDatePickerValue;
window.initConfigureComboBox = initConfigureComboBox;
window.configureComboBoxOpening = configureComboBoxOpening;
window.initInputFocus = initInputFocus;
window.setControlData = setControlData;
window.toggleVisibleWindow = toggleVisibleWindow;
window.initializeDateWidgets = initializeDateWidgets;
